import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from './button';

/**
 * The three states every data screen has (M0-24).
 *
 * They live here so screens do not each invent their own spinner and their own
 * phrasing for "something went wrong". Every later screen reuses these — if you
 * find yourself writing `{isPending ? …}` markup inline, use these instead.
 *
 * All three are announced to assistive technology: a screen reader user should
 * learn that something is loading, or that it failed, without seeing the layout.
 */

export function LoadingState({
  label,
  className,
}: {
  label?: string;
  className?: string;
}) {
  const { t } = useTranslation();

  return (
    <div
      className={cn('flex items-center gap-3 py-10 text-sm', className)}
      role="status"
      aria-live="polite"
    >
      <span
        className="size-4 animate-spin rounded-full border-2 border-[var(--color-border)] border-t-[var(--color-primary)]"
        aria-hidden="true"
      />
      <span className="text-[var(--color-muted-foreground)]">{label ?? t('states.loading')}</span>
    </div>
  );
}

/**
 * Nothing to show yet — which is different from something being broken. Empty
 * states say what would be here and, where there is one, offer the action that
 * fills it.
 */
export function EmptyState({
  title,
  description,
  action,
  className,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-2 rounded-lg border border-dashed px-6 py-12 text-center',
        className,
      )}
      data-testid="empty-state"
    >
      <p className="font-medium">{title}</p>
      {description ? (
        <p className="max-w-prose text-sm text-[var(--color-muted-foreground)]">{description}</p>
      ) : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

/**
 * Something failed. Says so plainly and offers the retry, rather than leaving a
 * screen that looks merely empty — an office manager who cannot tell "no jobs"
 * from "jobs did not load" will trust neither.
 */
export function ErrorState({
  title,
  description,
  onRetry,
  className,
}: {
  title?: string;
  description?: string;
  onRetry?: () => void;
  className?: string;
}) {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        'flex flex-col items-start gap-2 rounded-lg border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/5 px-4 py-4',
        className,
      )}
      role="alert"
      data-testid="error-state"
    >
      <p className="text-sm font-medium text-[var(--color-destructive)]">
        {title ?? t('states.errorTitle')}
      </p>
      <p className="text-sm text-[var(--color-muted-foreground)]">
        {description ?? t('states.errorDescription')}
      </p>
      {onRetry ? (
        <Button variant="outline" size="sm" className="mt-1" onClick={onRetry}>
          {t('states.tryAgain')}
        </Button>
      ) : null}
    </div>
  );
}
